import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getDefaultAvailabilityState,
  fromScheduleDto,
  toScheduleUpdateBody,
  saveAvailabilityState,
  dropPastDayOffs,
  type AvailabilityBlock,
  type AvailabilityState,
  type ScheduleDto,
} from '@/app/dashboard/settings/_ui/availabilityStorage';
import { saveFullSchedule } from '@/app/actions/dashboard';
import {
  addBlock as addBlockTo,
  blocksByDay as computeBlocksByDay,
  removeBlock as removeBlockFrom,
  setDayEnabled as withDayEnabled,
  type BlockSet,
  updateBlock as updateBlockIn,
  validateBlocks as validateBlocksInternal,
} from '@/lib/availability/blocks';
import type { DayOffRange } from '@peach/types/schedule/day-off.js';
import { callAction } from '@/lib/call-action';

const dayOffKey = (r: DayOffRange) => `${r.from}|${r.to}`;

interface AvailabilityStore {
  blocks: AvailabilityBlock[];
  disabledDays: Set<number>;
  dayOffs: DayOffRange[];
  bufferMinutes: number;
  dailySessionCap: number | null;
  savedBlocks: AvailabilityBlock[];
  savedDisabledDays: Set<number>;
  savedDayOffs: DayOffRange[];
  savedBufferMinutes: number;
  blocksByDay: Map<number, AvailabilityBlock[]>;
  hasChanges: boolean;
  hasDayOffChanges: boolean;
  blockErrors: Map<string, string>;
  generalError: string | null;
  saving: boolean;
  savingDayOffs: boolean;
  timezone: string;
  tutorUsername: string;
  initialize: (schedule: ScheduleDto | null, tutorUsername: string) => void;
  setTimezone: (tz: string) => void;
  /** Sets buffer minutes and auto-saves to backend (does not trigger main save button) */
  setBufferMinutes: (minutes: number) => void;
  /** Sets daily session cap and auto-saves to backend (does not trigger main save button) */
  setDailySessionCap: (cap: number | null) => void;
  addBlock: (day: number) => void;
  removeBlock: (blockId: string) => void;
  updateBlock: (
    blockId: string,
    patch: Partial<Pick<AvailabilityBlock, 'start' | 'end' | 'day'>>
  ) => void;
  setDayEnabled: (day: number, enabled: boolean) => void;
  /** Replaces the whole week in one piece, for the storefront's editor. */
  setWeek: (week: BlockSet) => void;
  addDayOff: (range: DayOffRange) => void;
  removeDayOff: (range: DayOffRange) => void;
  validateBlocks: () => void;
  setBlockErrors: (errors: Map<string, string>) => void;
  setGeneralError: (error: string | null) => void;
  saveSchedule: () => Promise<boolean>;
  saveDayOffs: () => Promise<boolean>;
}

function normalizeAvailabilityBlocks(
  blocks: AvailabilityBlock[],
  disabledDays: Set<number>,
  tutorUsername: string
) {
  let changed = false;
  const next = [...blocks];
  for (let day = 0; day <= 6; day += 1) {
    if (disabledDays.has(day)) continue;
    const hasAny = next.some((b) => b.day === day);
    if (!hasAny) {
      changed = true;
      const id = `ab_${tutorUsername}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      next.push({ id, day, start: '09:00', end: '17:00' });
    }
  }
  return changed ? next : blocks;
}

function computeHasChanges(
  blocks: AvailabilityBlock[],
  savedBlocks: AvailabilityBlock[],
  disabledDays: Set<number>,
  savedDisabledDays: Set<number>
): boolean {
  if (disabledDays.size !== savedDisabledDays.size) return true;
  for (const day of disabledDays) {
    if (!savedDisabledDays.has(day)) return true;
  }

  const serializeBlocks = (b: AvailabilityBlock[]) =>
    b
      .map((bl) => `${bl.day}:${bl.start}-${bl.end}`)
      .sort()
      .join('|');

  return serializeBlocks(blocks) !== serializeBlocks(savedBlocks);
}

function computeHasDayOffChanges(dayOffs: DayOffRange[], savedDayOffs: DayOffRange[]): boolean {
  if (dayOffs.length !== savedDayOffs.length) return true;
  const sortedDraft = dayOffs.map(dayOffKey).sort();
  const sortedSaved = savedDayOffs.map(dayOffKey).sort();
  return sortedDraft.some((d, i) => d !== sortedSaved[i]);
}

// Tracks the latest setDailySessionCap request. Any in-flight request with a
// lower ID is considered stale and its result is discarded on completion.
let capRequestId = 0;

export const useAvailabilityStore = create<AvailabilityStore>()(
  subscribeWithSelector((set, get) => ({
    blocks: [],
    disabledDays: new Set<number>(),
    dayOffs: [],
    bufferMinutes: 0,
    dailySessionCap: null,
    savedBlocks: [],
    savedDisabledDays: new Set<number>(),
    savedDayOffs: [],
    savedBufferMinutes: 0,
    blocksByDay: new Map<number, AvailabilityBlock[]>(),
    hasChanges: false,
    hasDayOffChanges: false,
    blockErrors: new Map<string, string>(),
    generalError: null,
    saving: false,
    savingDayOffs: false,
    timezone: '',
    tutorUsername: '',

    initialize: (schedule, tutorUsername) => {
      const state = schedule
        ? fromScheduleDto(schedule, tutorUsername)
        : getDefaultAvailabilityState(tutorUsername);

      const disabled = new Set(state.disabledDays);
      const normalized = normalizeAvailabilityBlocks(state.blocks, disabled, tutorUsername);
      const dayOffs = state.dayOffs ?? [];
      const bufferMinutes = state.bufferMinutes ?? 0;
      const dailySessionCap = state.dailySessionCap ?? null;

      set({
        blocks: normalized,
        disabledDays: disabled,
        dayOffs,
        bufferMinutes,
        dailySessionCap,
        savedBlocks: normalized,
        savedDisabledDays: disabled,
        savedDayOffs: dayOffs,
        savedBufferMinutes: bufferMinutes,
        tutorUsername,
        blockErrors: new Map(),
        generalError: null,
        blocksByDay: computeBlocksByDay(normalized),
        hasChanges: false,
        hasDayOffChanges: false,
      });
    },

    setTimezone: (tz) => {
      set({ timezone: tz });
    },

    setBufferMinutes: (minutes) => {
      const { tutorUsername, timezone, blocks, disabledDays, dayOffs, dailySessionCap } = get();

      set({ bufferMinutes: minutes });

      (async () => {
        const result = await callAction(() =>
          saveFullSchedule({ bufferMinutes: minutes, timezone }, tutorUsername)
        );

        if (result.success) {
          set({ savedBufferMinutes: minutes });

          const state: AvailabilityState = {
            blocks,
            disabledDays: Array.from(disabledDays),
            timezone,
            dayOffs,
            bufferMinutes: minutes,
            dailySessionCap,
          };
          saveAvailabilityState(tutorUsername, state);
        }
      })();
    },

    setDailySessionCap: (cap) => {
      const {
        tutorUsername,
        timezone,
        blocks,
        disabledDays,
        dayOffs,
        bufferMinutes,
        dailySessionCap: prevCap,
      } = get();

      // Tag this request so stale responses from older calls are discarded.
      const myRequestId = ++capRequestId;
      set({ dailySessionCap: cap });

      (async () => {
        const result = await callAction(() =>
          saveFullSchedule({ dailySessionCap: cap, timezone }, tutorUsername)
        );

        // Discard result if a newer request has already been made.
        if (capRequestId !== myRequestId) return;

        if (result.success) {
          set({ generalError: null });
          const state: AvailabilityState = {
            blocks,
            disabledDays: Array.from(disabledDays),
            timezone,
            dayOffs,
            bufferMinutes,
            dailySessionCap: cap,
          };
          saveAvailabilityState(tutorUsername, state);
        } else {
          set({ dailySessionCap: prevCap, generalError: 'Failed to save daily session cap' });
        }
      })();
    },

    addBlock: (day) => {
      const { tutorUsername, blocks, disabledDays, savedBlocks, savedDisabledDays } = get();
      const next = addBlockTo({ blocks, disabledDays }, day, `ab_${tutorUsername}`);
      set({
        blocks: next.blocks,
        blocksByDay: computeBlocksByDay(next.blocks),
        hasChanges: computeHasChanges(next.blocks, savedBlocks, disabledDays, savedDisabledDays),
      });
    },

    removeBlock: (blockId) => {
      const { blocks, disabledDays, savedBlocks, savedDisabledDays } = get();
      const next = removeBlockFrom({ blocks, disabledDays }, blockId);
      set({
        blocks: next.blocks,
        disabledDays: next.disabledDays,
        blocksByDay: computeBlocksByDay(next.blocks),
        hasChanges: computeHasChanges(
          next.blocks,
          savedBlocks,
          next.disabledDays,
          savedDisabledDays
        ),
      });
    },

    updateBlock: (blockId, patch) => {
      const { blocks, disabledDays, savedBlocks, savedDisabledDays } = get();
      const next = updateBlockIn({ blocks, disabledDays }, blockId, patch);
      set({
        blocks: next.blocks,
        blocksByDay: computeBlocksByDay(next.blocks),
        hasChanges: computeHasChanges(next.blocks, savedBlocks, disabledDays, savedDisabledDays),
      });
    },

    setDayEnabled: (day, enabled) => {
      const { tutorUsername, blocks, disabledDays, savedBlocks, savedDisabledDays } = get();
      const next = withDayEnabled({ blocks, disabledDays }, day, enabled, `ab_${tutorUsername}`);
      set({
        disabledDays: next.disabledDays,
        blocks: next.blocks,
        blocksByDay: computeBlocksByDay(next.blocks),
        hasChanges: computeHasChanges(
          next.blocks,
          savedBlocks,
          next.disabledDays,
          savedDisabledDays
        ),
      });
    },

    setWeek: ({ blocks, disabledDays }) => {
      const { savedBlocks, savedDisabledDays } = get();
      set({
        blocks,
        disabledDays,
        blocksByDay: computeBlocksByDay(blocks),
        hasChanges: computeHasChanges(blocks, savedBlocks, disabledDays, savedDisabledDays),
      });
    },

    addDayOff: (range) => {
      const { dayOffs, savedDayOffs } = get();
      const key = dayOffKey(range);
      if (dayOffs.some((r) => dayOffKey(r) === key)) return;
      const newDayOffs = [...dayOffs, range].sort((a, b) => a.from.localeCompare(b.from));

      set({
        dayOffs: newDayOffs,
        hasDayOffChanges: computeHasDayOffChanges(newDayOffs, savedDayOffs),
      });
    },

    removeDayOff: (range) => {
      const { dayOffs, savedDayOffs } = get();
      const key = dayOffKey(range);
      const newDayOffs = dayOffs.filter((r) => dayOffKey(r) !== key);

      set({
        dayOffs: newDayOffs,
        hasDayOffChanges: computeHasDayOffChanges(newDayOffs, savedDayOffs),
      });
    },

    validateBlocks: () => {
      const { blocks } = get();
      const errors = validateBlocksInternal(blocks);
      set({ blockErrors: errors });

      if (errors.size === 0) {
        set((state) => {
          if (!state.generalError) return state;
          const isValidationError =
            state.generalError.includes('Overlaps') ||
            state.generalError.includes('Set a start and an end') ||
            state.generalError.includes('must be after start') ||
            state.generalError.includes('Invalid day');
          return isValidationError ? { generalError: null } : state;
        });
      }
    },

    setBlockErrors: (errors) => {
      set({ blockErrors: errors });
    },

    setGeneralError: (error) => {
      set({ generalError: error });
    },

    saveSchedule: async () => {
      const {
        blocks,
        disabledDays,
        timezone,
        tutorUsername,
        dayOffs,
        bufferMinutes,
        dailySessionCap,
      } = get();

      const errors = validateBlocksInternal(blocks);
      if (errors.size > 0) {
        set({ blockErrors: errors });
        return false;
      }

      const state: AvailabilityState = {
        blocks,
        disabledDays: Array.from(disabledDays),
        timezone,
        bufferMinutes,
        dailySessionCap,
      };

      set({ saving: true, generalError: null });

      const body = toScheduleUpdateBody(state, timezone);
      delete body.dayOffs;
      const result = await callAction(() => saveFullSchedule(body, tutorUsername));

      if (!result.success) {
        set({ saving: false, generalError: result.error });
        return false;
      }

      // Saved is the snapshot sent, not the week now: an edit made while the
      // request was out is still owed.
      const now = get();
      set({
        saving: false,
        savedBlocks: blocks,
        savedDisabledDays: new Set(disabledDays),
        hasChanges: computeHasChanges(now.blocks, blocks, now.disabledDays, disabledDays),
      });

      saveAvailabilityState(tutorUsername, { ...state, dayOffs });
      return true;
    },

    saveDayOffs: async () => {
      const {
        dayOffs,
        timezone,
        tutorUsername,
        blocks,
        disabledDays,
        bufferMinutes,
        dailySessionCap,
      } = get();

      // Self-clean: drop day offs that ended before today.
      const pruned = dropPastDayOffs(dayOffs);

      set({ savingDayOffs: true, generalError: null });

      const body = { dayOffs: pruned, timezone };
      const result = await callAction(() => saveFullSchedule(body, tutorUsername));

      if (!result.success) {
        set({ savingDayOffs: false, generalError: result.error });
        return false;
      }

      set({
        savingDayOffs: false,
        dayOffs: pruned,
        savedDayOffs: pruned,
        hasDayOffChanges: false,
      });

      const state: AvailabilityState = {
        blocks,
        disabledDays: Array.from(disabledDays),
        timezone,
        dayOffs: pruned,
        bufferMinutes,
        dailySessionCap,
      };
      saveAvailabilityState(tutorUsername, state);
      return true;
    },
  }))
);

let validationTimeout: ReturnType<typeof setTimeout> | null = null;

useAvailabilityStore.subscribe(
  (state) => state.blocks,
  (blocks) => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    if (blocks.length === 0) {
      useAvailabilityStore.setState({ blockErrors: new Map() });
      return;
    }

    validationTimeout = setTimeout(() => {
      useAvailabilityStore.getState().validateBlocks();
    }, 150);
  }
);

export const useBlocksByDay = () => useAvailabilityStore((s) => s.blocksByDay);
export const useHasChanges = () => useAvailabilityStore((s) => s.hasChanges);
export const useHasDayOffChanges = () => useAvailabilityStore((s) => s.hasDayOffChanges);
